import React, { useState, useContext } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Button } from '@tymate/elise';
import { Container, EmptyState, theme, Title, Buttons } from '@tymate/elise/ui';
import { Modal } from '@tymate/elise/components';
import CollectionPointsTreeWrapper from 'components/CollectionPointsTree';
import PdfPage from 'components/PdfPage';
import { isEmpty } from 'lodash';
import { CollectionPointContext } from 'contexts';
import { useReporting } from 'contexts/ReportingContext';
import { getReportings, getCollectedResourcesCSV } from 'api/reporting';
import ReportingGraph from 'components/ReportingGraph';
import {
  ReportingHeader,
  ReportingCard,
  ReportingContent,
  Values,
  TextValue,
  Circle,
  Label,
  Equivalences,
  EquivalencesTitle,
  Equivalence,
  EquivalencesList,
  ReportingItem,
} from 'ui';
import { useQuery } from 'react-query';
import { RiTableFill } from 'react-icons/ri';
import { MdFileDownload } from 'react-icons/md';
import { Stack, Spinner } from '@tymate/margaret';
import { endOfDay, format } from 'date-fns';
import { useDeepCompareEffect } from 'react-use';
import { formatNumberToPrettyString, formatDate } from '@tymate/elise/utils';
import ReportingParams from 'components/ReportingParams';
import { saveAs } from 'file-saver';
import { useAppState } from '@tymate/elise/hooks';

import icCo2 from 'images/equivalence-co2.svg';
import icEnergy from 'images/equivalence-energy.svg';
import icOil from 'images/equivalence-oil.svg';
import icWater from 'images/equivalence-water.svg';
import icWork from 'images/equivalence-work.svg';

import IcTrain from 'images/icons/IcTrain';
import IcCar from 'images/icons/IcCar';
import IcComputer from 'images/icons/IcComputer';
import IcPeople from 'images/icons/IcPeople';
import IcShower from 'images/icons/IcShower';

const filterReportings = reportings =>
  (reportings || []).filter(({ label }) => label);

const filterReportingsDataTable = reportings => {
  const filteredReportings = {};

  for (const key in reportings) {
    const labels = reportings[key].map(({ label }) => label).filter(Boolean);

    if (labels.length > 0) {
      filteredReportings[key] = reportings[key];
    }
  }
  return filteredReportings;
};

const HomeReporting = () => {
  const { collectionPoint } = useContext(CollectionPointContext);
  const { reportingParams, updateReportingParams } = useReporting();
  const { notify } = useAppState();
  const [loading, setLoading] = useState(false);

  const [
    collectionPointsSelectModalIsShown,
    setCollectionPointsSelectModalIsShown,
  ] = useState(false);
  const [tempParams, setTempParams] = useState(null);
  const [canvas, setCanvas] = useState();
  const [canvasDataUrl, setCanvasDataUrl] = useState();

  const paramsAreFilled =
    Boolean(reportingParams?.startAt) && Boolean(reportingParams?.endAt);

  const { data, isLoading, isSuccess } = useQuery(
    ['reporting', reportingParams],
    () =>
      getReportings({
        params: { ...reportingParams, endAt: endOfDay(reportingParams.endAt) },
      }),
    {
      enabled: paramsAreFilled,
    },
  );

  const reportings = filterReportings(data?.data?.weighingGraph);
  const reportingsDataTable = filterReportingsDataTable(
    data?.data?.wasteWeights,
  );

  const equivalences = data?.data?.equivalences ?? [];

  useDeepCompareEffect(() => {
    setTimeout(() => {
      setCanvas(document.querySelector('canvas'));
      setCanvasDataUrl(canvas?.toDataURL());
    }, 200);
    return () => clearTimeout();
  }, [{ reportings, canvas }]);

  const generateFilename = () => {
    const fromDate = format(reportingParams.startAt, 'MM/yy');
    const toDate = format(reportingParams.endAt, 'MM/yy');
    const closestCommonParent =
      reportingParams.closestCommonParent || collectionPoint?.name;
    return `reporting-${
      fromDate === toDate ? fromDate : `${fromDate}-${toDate}`
    }-${closestCommonParent}`;
  };

  const handleExtract = async (fused = true) => {
    try {
      setLoading(true);
      const response = await getCollectedResourcesCSV({
        ...reportingParams,
        filename: generateFilename(),
        fused,
      });
      const { data, status } = response;
      const headers = response.headers || {};

      if (status === 200) {
        notify('Extraction complète', {
          type: 'info',
        });

        const contentType = headers['contentType'] || 'text/csv;charset=utf-8';
        const filename =
          headers['content-disposition']
            ?.split('filename=')[1]
            ?.replace(/"/g, '') || generateFilename();

        if (contentType.includes('application/zip')) {
          const blob = new Blob([data], {
            type: 'application/zip',
          });
          saveAs(blob, filename + '.zip');
        } else {
          const csvData = '\uFEFF' + data;
          const blob = new Blob([csvData], {
            type: 'text/csv;charset=utf-8',
          });
          saveAs(blob, filename + '.csv');
        }
      } else {
        handleWarningOrError(status, data.message);
      }
    } catch (e) {
      console.error('Extraction error:', e);
      handleWarningOrError(e.response?.status, e.response?.data?.['error']);
    } finally {
      setLoading(false);
    }
  };

  const handleWarningOrError = (status, message) => {
    if (status === 204) {
      notify("Pas de données correspondant à l'extraction", {
        type: 'warning',
      });
    } else if (status === 400 && message === 'Too much content') {
      notify('Trop de données, affinez la recherche', {
        type: 'warning',
      });
    } else if (status === 400 && message === 'Invalid parameters') {
      notify("Paramètres de l'extraction invalides", {
        type: 'error',
      });
    } else {
      notify('Une erreur est survenue, veuillez rééssayer ultérieurement.', {
        type: 'error',
      });
    }
  };

  return (
    <Container variant="main">
      <Stack
        size="full"
        alignX="space-between"
        alignY="flex-start"
        direction={{ default: 'column', medium: 'row' }}
      >
        <ReportingParams showCollectionPointSelect={false} />

        {reportings.length > 0 && (
          <Stack direction="column" gutterSize={1}>
            <Button
              variant="outline"
              type="button"
              disabled={!paramsAreFilled || loading}
              onClick={() => handleExtract(true)}
              loading={loading}
              icon={<RiTableFill />}
            >
              Télécharger le CSV
            </Button>
            <PDFDownloadLink
              style={{ textDecoration: 'none' }}
              document={
                Boolean(canvasDataUrl) && (
                  <PdfPage
                    reportings={reportings}
                    equivalences={equivalences}
                    reportingsDataTable={reportingsDataTable}
                    canvasUrl={canvasDataUrl}
                    params={{ ...reportingParams, collectionPoint }}
                  />
                )
              }
              fileName="Reporting-document.pdf"
            >
              {({ loading }) => {
                return (
                  <Button
                    variant="outline"
                    as="span"
                    icon={<MdFileDownload />}
                    disabled={
                      !(
                        paramsAreFilled &&
                        Boolean(canvasDataUrl) &&
                        !isEmpty(reportings) &&
                        !isEmpty(reportingsDataTable)
                      ) || loading
                    }
                  >
                    Télécharger le PDF
                  </Button>
                );
              }}
            </PDFDownloadLink>
          </Stack>
        )}
      </Stack>
      <Stack marginTop={2}>
        <Title>
          {(() => {
            return reportingParams.closestCommonParent;
          })()}
        </Title>
      </Stack>
      <ReportingCard>
        <ReportingHeader>{`Bilan recyclage du ${formatDate(
          reportingParams?.startAt,
          'dd/MM/yyyy',
        )} au ${formatDate(
          reportingParams?.endAt,
          'dd/MM/yyyy',
        )}`}</ReportingHeader>

        {isLoading && <Spinner />}

        {isSuccess && reportings.length === 0 && (
          <EmptyState>Pas de données pour cette sélection</EmptyState>
        )}

        {isSuccess && reportings.length !== 0 && (
          <ReportingContent>
            <ReportingGraph data={reportings} />
            <Stack>
              <ReportingItem>
                {reportings.map(({ color, label }, index) => (
                  <Label key={index}>
                    <Circle color={Boolean(color) ? color : theme.primary} />
                    <span>{label}</span>
                  </Label>
                ))}
              </ReportingItem>
              <Values>
                {reportings.map(({ value }, index) => (
                  <TextValue key={index}>
                    {`${formatNumberToPrettyString(value)} kg`}
                  </TextValue>
                ))}
              </Values>
            </Stack>
          </ReportingContent>
        )}
      </ReportingCard>
      {!isEmpty(equivalences) && (
        <Equivalences>
          <EquivalencesTitle>
            Les bénéfices environnementaux et sociaux de votre action
          </EquivalencesTitle>
          <EquivalencesList>
            <Equivalence>
              <img src={icCo2} alt="co2" />
              <div>
                {`${formatNumberToPrettyString(equivalences?.co2)} kg de CO2`}
              </div>
              <div>=</div>
              <IcTrain />
              <div>{`${formatNumberToPrettyString(
                equivalences?.kmTrain,
              )} km en train`}</div>
            </Equivalence>
            <Equivalence>
              <img src={icOil} alt="pétrole" />
              <div>
                {`${formatNumberToPrettyString(
                  equivalences?.oil,
                )} litres de pétrole`}
              </div>
              <div>=</div>
              <IcCar />
              <div>{`${formatNumberToPrettyString(
                equivalences?.kmCar,
              )} km en voiture essence`}</div>
            </Equivalence>
            <Equivalence>
              <img src={icEnergy} alt="énergie" />
              <div>
                {`${formatNumberToPrettyString(
                  equivalences?.energy,
                )} kWh d'énergie`}
              </div>
              <div>=</div>
              <IcComputer />
              <div>{`${formatNumberToPrettyString(
                equivalences?.pc,
              )} h d'utilisation d'un PC`}</div>
            </Equivalence>
            <Equivalence>
              <img src={icWater} alt="eau" />
              <div>
                {`${formatNumberToPrettyString(
                  equivalences?.water,
                )} litres d'eau`}
              </div>
              <div>=</div>
              <IcShower />
              <div>{`${formatNumberToPrettyString(
                equivalences?.shower,
              )} douches de 5 min`}</div>
            </Equivalence>
            <Equivalence>
              <img src={icWork} alt="travail" />
              <div>
                {`${formatNumberToPrettyString(
                  equivalences?.job,
                )} heures de travail`}
              </div>
              <div>=</div>
              <IcPeople />
              <div>{` ${formatNumberToPrettyString(
                equivalences?.etp,
              )} ETP mensuel`}</div>
            </Equivalence>
          </EquivalencesList>
        </Equivalences>
      )}
      {collectionPointsSelectModalIsShown && (
        <Modal
          isOpen
          variant="medium"
          title="Sélectionnez les points de collecte"
          onRequestClose={() => setCollectionPointsSelectModalIsShown(false)}
        >
          <CollectionPointsTreeWrapper
            collectionPoint={collectionPoint}
            defaultSelectedSelectionPointIds={tempParams}
            onChange={({ collectionPointIds, additionalCollectionPointsIds }) => {
              setTempParams({
                collectionPointIds,
                additionalCollectionPointsIds,
              });
            }}
          />
          <Buttons alignment="center" hasMarginTop>
            <Button
              variant="primary"
              onClick={() => {
                updateReportingParams({
                  ...reportingParams,
                  collectionPointIds: tempParams.collectionPointIds,
                  additionalCollectionPointsIds: tempParams.additionalCollectionPointsIds,
                });
                setTempParams(null);
                setCollectionPointsSelectModalIsShown(false);
              }}
            >
              Valider
            </Button>
          </Buttons>
        </Modal>
      )}
    </Container>
  );
};

export default HomeReporting;
